import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Grid,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@mui/material';
import MuiButton from 'app/design/uiComponents/MuiButton';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { ItemHead, ItemSeparator } from 'app/design/speedBot/EntityItem';
import { StrategyBuilderEntryExitFormData, UserBot } from './types';
import StrategyBuilder from 'app/components/Builder/Strategy/StrategyBuilder';
import {
  FieldData,
  FieldDataConditions,
  FieldDataWithValidate,
} from 'app/components/Builder/Strategy/fields/types';
import { StrategyMode } from 'app/components/Builder/Strategy/utils/strategy-data';
import { validateConditions } from 'app/components/Builder/Strategy/utils/StrategyValidation';
import { Condition } from 'app/components/Builder/Strategy/types';
import SnakbarMessageModal from 'app/design/uiComponents/SnakbarMessageModal';
import { isValidConditions, PositionDirectionData, RiskTypeData } from './util';
// import { FeatureFlag } from 'app/components/Common/FeatureFlag';
// import { FEATURES } from 'types/Feature';
import { useTranslation } from 'react-i18next';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';
import { useGetMediaQueryUp } from 'app/hooks/useGetMediaQuery';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomTooltip from 'app/design/uiComponents/CustomTooltip';
import {
  FeatureFlag,
  useIsFeatureFlag,
} from 'app/components/Common/FeatureFlag';
import { FEATURES } from 'types/Feature';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DialogContentText from '@mui/material/DialogContentText';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import chartinklogo from '../../../../assets/images/chartink-logo.png';

// Chartink logo path - from public folder
// const chartinkLogo = '/chartink-logo.png';

export const PositionDirectionCondition = ({
  title,
  conditionsData,
  isConditionValid,
  setConditionsDataWithValid,
  error,
  updateCopiedStrategy,
  copiedStrategy,
  tooltipKey,
  defaultResolution,
  chartinkHandler, // <-- add as prop
  chartinkDisabled,
  onChartinkRemoved, // <-- add this prop
  blockType, // <-- add as prop
  positionDirection, // <-- add as prop
  onRemoveChartinkBoth, // <-- add as prop
  exitType, // <-- add as prop
  onRemoveChartinkBothExit, // <-- add as prop
  entryType, // <-- add as prop
  onRemoveChartinkBothEntry, // <-- add as prop
}: {
  title: string;
  tooltipKey?: string;
  conditionsData: FieldData[];
  isConditionValid: boolean;
  setConditionsDataWithValid: (values: FieldDataWithValidate) => void;
  error?: string;
  updateCopiedStrategy: (strategy: Condition) => void;
  copiedStrategy: Condition | undefined;
  defaultResolution?: string;
  chartinkHandler?: () => void;
  chartinkDisabled: boolean;
  onChartinkRemoved?: () => void; // <-- add to type
  blockType: 'entry' | 'exit';
  positionDirection?: string; // <-- add to type
  onRemoveChartinkBoth?: () => void; // <-- add to type
  exitType?: 'long' | 'short';
  onRemoveChartinkBothExit?: (exitType: 'long' | 'short') => void;
  entryType?: 'long' | 'short';
  onRemoveChartinkBothEntry?: (entryType: 'long' | 'short') => void;
}) => {
  const setConditions = (conditions: FieldData[]) => {
    const isCorrect = validateConditions(conditions);
    setConditionsDataWithValid({
      conditions: conditions,
      isConditionValid: isCorrect,
    });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box display={'flex'} alignItems={'center'}>
            <ItemHead variant="small" title={title} />
            <CustomTooltip tooltipKey={tooltipKey}>
              {({ isTooltipAvailable }) =>
                isTooltipAvailable ? (
                  <IconButton>
                    <InfoOutlinedIcon />
                  </IconButton>
                ) : (
                  <></>
                )
              }
            </CustomTooltip>
          </Box>
        </Box>
        <Box className="m-main-spacing">
          <ItemSeparator variant="1px" />
        </Box>
      </Box>
      <Box className="m-main-spacing" sx={{ mb: 1 }}>
        <StrategyBuilder
          setStrategy={setConditions}
          inputConditions={conditionsData}
          updateCopiedStrategy={updateCopiedStrategy}
          copiedStrategy={copiedStrategy}
          mode={StrategyMode.EDIT}
          isValid={isConditionValid}
          defaultResolution={defaultResolution}
          onChartinkRemoved={onChartinkRemoved}
          blockType={blockType}
          positionDirection={positionDirection}
          exitType={exitType}
          onRemoveChartinkBothExit={onRemoveChartinkBothExit}
          entryType={entryType}
          onRemoveChartinkBothEntry={onRemoveChartinkBothEntry}
        />
      </Box>
      {error && (
        <Box className="m-main-spacing">
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </Box>
  );
};

export default function StrategyBuilderEntryExitTab({
  entryExitData,
  setEntryExit,
  handleBack,
  defaultResolution,
  userBotDetails,
  isEditMode,
}: {
  entryExitData: StrategyBuilderEntryExitFormData;
  setEntryExit: (values: StrategyBuilderEntryExitFormData) => void;
  handleBack: (values: number) => void;
  defaultResolution?: string;
  userBotDetails?: UserBot;
  isEditMode?: boolean;
}) {
  const { t } = useTranslation();
  const isMdUp = useGetMediaQueryUp('md');
  const handleContinue = (values: StrategyBuilderEntryExitFormData) => {
    setEntryExit(values);
  };

  const [copiedStrategy, setCopiedStrategy] = React.useState<Condition>();
  const [invalidConditionDirection, setInvalidConditionDirection] =
    React.useState<string>('');
  const handleInvalidConditionDirection = () => {
    setInvalidConditionDirection('');
  };

  // --- Chartink common button state ---
  const [showChartinkDialog, setShowChartinkDialog] = useState(false);
  const [chartinkExitAllowed, setChartinkExitAllowed] = useState(false);
  const [chartinkEnabled, setChartinkEnabled] = useState(false);
  // For showing Chartink URLs in edit mode
  const strategyId = userBotDetails?.strategy_id;
  const isChartinkEnabled = useIsFeatureFlag([FEATURES.IS_CHARTINK_ENABLED]);

  // Helper to build Chartink URL
  const getChartinkUrl = (direction: string, signalType: number) =>
    `/chartink/${strategyId || ''}/${direction}/${signalType}`;

  // --- Formik state for Chartink logic ---
  const formikRef =
    React.useRef<FormikProps<StrategyBuilderEntryExitFormData>>(null);

  // Chartink effect: runs when chartinkEnabled, chartinkExitAllowed, or positionDirection changes
  useEffect(() => {
    if (!formikRef.current) return;
    const values = formikRef.current.values;
    const setFieldValue = formikRef.current.setFieldValue;
    const validateForm = formikRef.current.validateForm;
    // Debug: Chartink add logic

    if (chartinkEnabled) {
      const chartinkCondition: FieldData = {
        key: 'chartink',
        type: 'external_signal',
        params: { label: 'chartink' },
      };

      // Helper to add Chartink only if not present
      const addChartink = (oldConditions: FieldData[] = [], isExit = false) => {
        if (oldConditions.some(c => c.key === 'chartink')) return oldConditions;
        // Always return only Chartink, don't append to existing conditions
        return [chartinkCondition];
      };
      if (values.positionDirection === 'long') {
        // Entry: only Chartink
        setFieldValue(
          'longEntryConditions',
          {
            conditions: addChartink(
              values.longEntryConditions?.conditions || [],
              false,
            ),
            isConditionValid: true,
          },
          false,
        );
        // Exit: Chartink + normal
        if (chartinkExitAllowed)
          setFieldValue(
            'longExitConditions',
            {
              conditions: addChartink(
                values.longExitConditions?.conditions || [],
                true,
              ),
              isConditionValid: true,
            },
            false,
          );
        setTimeout(() => {
          void validateForm();
        }, 0);
      } else if (values.positionDirection === 'short') {
        setFieldValue(
          'shortEntryConditions',
          {
            conditions: addChartink(
              values.shortEntryConditions?.conditions || [],
              false,
            ),
            isConditionValid: true,
          },
          false,
        );
        if (chartinkExitAllowed)
          setFieldValue(
            'shortExitConditions',
            {
              conditions: addChartink(
                values.shortExitConditions?.conditions || [],
                true,
              ),
              isConditionValid: true,
            },
            false,
          );
        setTimeout(() => {
          void validateForm();
        }, 0);
      } else if (values.positionDirection === 'both') {
        setFieldValue(
          'longEntryConditions',
          {
            conditions: addChartink(
              values.longEntryConditions?.conditions || [],
              false,
            ),
            isConditionValid: true,
          },
          false,
        );
        setFieldValue(
          'shortEntryConditions',
          {
            conditions: addChartink(
              values.shortEntryConditions?.conditions || [],
              false,
            ),
            isConditionValid: true,
          },
          false,
        );
        if (chartinkExitAllowed) {
          setFieldValue(
            'longExitConditions',
            {
              conditions: addChartink(
                values.longExitConditions?.conditions || [],
                true,
              ),
              isConditionValid: true,
            },
            false,
          );
          setFieldValue(
            'shortExitConditions',
            {
              conditions: addChartink(
                values.shortExitConditions?.conditions || [],
                true,
              ),
              isConditionValid: true,
            },
            false,
          );
        }
        setTimeout(() => {
          void validateForm();
        }, 0);
      }
    }
  }, [chartinkEnabled, chartinkExitAllowed]);

  // Add a callback to re-enable Chartink button when Chartink is removed
  const handleChartinkRemoved = () => {
    setChartinkEnabled(false);
  };

  // Reset chartinkExitAllowed when dialog opens
  useEffect(() => {
    if (showChartinkDialog) {
      // Check if Chartink exists in exit conditions
      const checkExitConditionsForChartink = () => {
        if (!formikRef.current) return false;
        const values = formikRef.current.values;

        const hasChartinkInLongExit = (
          values.longExitConditions?.conditions || []
        ).some(c => c.key === 'chartink');
        const hasChartinkInShortExit = (
          values.shortExitConditions?.conditions || []
        ).some(c => c.key === 'chartink');

        return hasChartinkInLongExit || hasChartinkInShortExit;
      };

      // Set checkbox based on whether Chartink exists in exit conditions
      setChartinkExitAllowed(checkExitConditionsForChartink());
    }
  }, [showChartinkDialog]);

  // Handler to remove Chartink from both long/short exit blocks only (not entry)
  const handleRemoveChartinkBothExit = (exitType: 'long' | 'short') => {
    if (!formikRef.current) return;
    const setFieldValue = formikRef.current.setFieldValue;
    const validateForm = formikRef.current.validateForm;

    // Simply remove Chartink and leave empty conditions
    setFieldValue(
      'longExitConditions',
      { conditions: [], isConditionValid: true },
      false,
    );
    setFieldValue(
      'shortExitConditions',
      { conditions: [], isConditionValid: true },
      false,
    );

    setTimeout(() => {
      void validateForm();
    }, 0);
    setChartinkEnabled(false);
  };

  // Handler to remove Chartink from both long/short entry blocks only (not exit)
  const handleRemoveChartinkBothEntry = (entryType: 'long' | 'short') => {
    if (!formikRef.current) return;
    const setFieldValue = formikRef.current.setFieldValue;
    const validateForm = formikRef.current.validateForm;

    // Simply remove Chartink and leave empty conditions
    setFieldValue(
      'longEntryConditions',
      { conditions: [], isConditionValid: true },
      false,
    );
    setFieldValue(
      'shortEntryConditions',
      { conditions: [], isConditionValid: true },
      false,
    );

    setTimeout(() => {
      void validateForm();
    }, 0);
    setChartinkEnabled(false);
  };

  return (
    <>
      {/* Chartink Dialog */}
      <Dialog
        open={showChartinkDialog}
        onClose={() => setShowChartinkDialog(false)}
      >
        <DialogTitle>Chartink Condition</DialogTitle>
        <DialogContent>
          {/* Check if there are normal conditions to show warning */}
          {(() => {
            if (!formikRef.current) return null;
            const values = formikRef.current.values;

            const hasNormalInLongEntry = (
              values.longEntryConditions?.conditions || []
            ).some(c => c.key !== 'chartink');
            const hasNormalInLongExit = (
              values.longExitConditions?.conditions || []
            ).some(c => c.key !== 'chartink');
            const hasNormalInShortEntry = (
              values.shortEntryConditions?.conditions || []
            ).some(c => c.key !== 'chartink');
            const hasNormalInShortExit = (
              values.shortExitConditions?.conditions || []
            ).some(c => c.key !== 'chartink');

            const hasNormalConditions =
              hasNormalInLongEntry ||
              hasNormalInLongExit ||
              hasNormalInShortEntry ||
              hasNormalInShortExit;

            return hasNormalConditions ? (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <DialogContentText sx={{ color: '#a67c00', fontWeight: 500 }}>
                  <WarningAmberIcon sx={{ lineHeight: 1 }} /> Warning: Adding
                  Chartink will remove all existing normal conditions from entry
                  and exit blocks.
                </DialogContentText>
              </Box>
            ) : null;
          })()}

          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              checked={chartinkExitAllowed}
              onChange={e => setChartinkExitAllowed(e.target.checked)}
              id="chartink-exit-checkbox"
            />
            <label htmlFor="chartink-exit-checkbox" style={{ marginLeft: 8 }}>
              {t('Allow Chartink in Exit conditions')}
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartinkDialog(false)} color="primary">
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => {
              setChartinkEnabled(true);
              setShowChartinkDialog(false);
            }}
            color="primary"
          >
            {t('OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chartink URLs in edit mode */}

      <Formik
        innerRef={formikRef}
        initialValues={{
          positionDirection: entryExitData.positionDirection
            ? entryExitData.positionDirection
            : '',
          targetType: entryExitData.targetType ? entryExitData.targetType : '',
          trailing_sl_enabled: entryExitData.trailing_sl_enabled
            ? entryExitData.trailing_sl_enabled
            : false,
          target:
            entryExitData && entryExitData.target
              ? entryExitData.target
              : undefined,
          stoploss: entryExitData.stoploss ? entryExitData.stoploss : undefined,
          trailingTriggerValue:
            entryExitData && entryExitData.trailingTriggerValue
              ? entryExitData.trailingTriggerValue
              : 1,
          trailingValue: entryExitData.trailingValue
            ? entryExitData.trailingValue
            : 1,
          isTrailStoploss: entryExitData.isTrailStoploss
            ? entryExitData.isTrailStoploss
            : false,
          trailStoploss: entryExitData.trailStoploss
            ? entryExitData.trailStoploss
            : undefined,
          shortEntryConditions: entryExitData.shortEntryConditions
            ? entryExitData.shortEntryConditions
            : ({
                conditions: [],
                isConditionValid: true,
              } as FieldDataWithValidate),
          shortExitConditions: entryExitData.shortExitConditions
            ? entryExitData.shortExitConditions
            : ({
                conditions: [],
                isConditionValid: true,
              } as FieldDataWithValidate),
          longEntryConditions: entryExitData.longEntryConditions
            ? entryExitData.longEntryConditions
            : ({
                conditions: [],
                isConditionValid: true,
              } as FieldDataWithValidate),
          longExitConditions: entryExitData.longExitConditions
            ? entryExitData.longExitConditions
            : ({
                conditions: [],
                isConditionValid: true,
              } as FieldDataWithValidate),
        }}
        validationSchema={Yup.object().shape({
          isTrailStoploss: Yup.boolean(),
          positionDirection: Yup.string().required(
            t('user_backtesting.valid_msg.req', {
              name: t('user_bot_startegy.form.position_direction'),
            }),
          ),
          targetType: Yup.string().required(
            t('user_backtesting.valid_msg.req', {
              name: t('user_bot_startegy.form.target_stop_loss_type'),
            }),
          ),
          target: Yup.number()
            .required(
              t('user_backtesting.valid_msg.req', {
                name: t('user_bot_startegy.form.target'),
              }),
            )
            .min(
              0,
              t('user_backtesting.valid_msg.valid', {
                name: t('user_bot_startegy.form.target'),
              }),
            ),
          stoploss: Yup.number()
            .required(
              t('user_backtesting.valid_msg.req', {
                name: t('user_bot_startegy.form.stoploss'),
              }),
            )
            .min(
              0,
              t('user_backtesting.valid_msg.valid', {
                name: t('user_bot_startegy.form.stoploss'),
              }),
            ),
          trailingTriggerValue: Yup.number()
            .required(
              t('user_backtesting.valid_msg.req', {
                name: t('user_bot_startegy.form.trailingTriggerValue'),
              }),
            )
            .min(
              0,
              t('user_backtesting.valid_msg.valid', {
                name: t('user_bot_startegy.form.trailingTriggerValue'),
              }),
            ),
          trailingValue: Yup.number()
            .required(
              t('user_backtesting.valid_msg.req', {
                name: t('user_bot_startegy.form.trailingValue'),
              }),
            )
            .min(
              0,
              t('user_backtesting.valid_msg.valid', {
                name: t('user_bot_startegy.form.trailingValue'),
              }),
            ),
          trailStoploss: Yup.number().when('isTrailStoploss', {
            is: true,
            then: Yup.number(),
          }),
          shortEntryConditions: Yup.object().when('positionDirection', {
            is: value => value === 'short' || value === 'both',
            then: Yup.object().shape({
              conditions: Yup.array()
                .of(Yup.mixed())
                .min(
                  1,
                  t('user_backtesting.valid_msg.req', {
                    name: t('user_bot_startegy.form.short_entry_condition'),
                  }),
                )
                .required(
                  t('user_backtesting.valid_msg.req', {
                    name: t('user_bot_startegy.form.short_entry_condition'),
                  }),
                ),
            }),
          }),
          longEntryConditions: Yup.object().when('positionDirection', {
            is: value => value === 'long' || value === 'both',
            then: Yup.object().shape({
              conditions: Yup.array()
                .of(Yup.mixed())
                .min(
                  1,
                  t('user_backtesting.valid_msg.req', {
                    name: t('user_bot_startegy.form.long_entry_condition'),
                  }),
                )
                .required(
                  t('user_backtesting.valid_msg.req', {
                    name: t('user_bot_startegy.form.long_entry_condition'),
                  }),
                ),
            }),
          }),
          longExitConditions: Yup.object().test(
            'chartink-exit-without-entry',
            t('user_bot_startegy.error.exit_only_chartink'),
            function (value) {
              const entry = (this.parent.longEntryConditions?.conditions ||
                []) as import('app/components/Builder/Strategy/fields/types').FieldData[];
              const exit = (value?.conditions ||
                []) as import('app/components/Builder/Strategy/fields/types').FieldData[];
              const hasChartinkExit = exit.some(c => c.key === 'chartink');
              const hasChartinkEntry = entry.some(c => c.key === 'chartink');
              if (hasChartinkExit && !hasChartinkEntry) {
                return this.createError({
                  message: t(
                    'You cannot add Chartink to the exit condition without first adding it to the entry condition',
                  ),
                });
              }
              return true;
            },
          ),
          shortExitConditions: Yup.object().test(
            'chartink-exit-without-entry',
            t('user_bot_startegy.error.exit_only_chtink'),
            function (value) {
              const entry = (this.parent.shortEntryConditions?.conditions ||
                []) as import('app/components/Builder/Strategy/fields/types').FieldData[];
              const exit = (value?.conditions ||
                []) as import('app/components/Builder/Strategy/fields/types').FieldData[];
              const hasChartinkExit = exit.some(c => c.key === 'chartink');
              const hasChartinkEntry = entry.some(c => c.key === 'chartink');
              if (hasChartinkExit && !hasChartinkEntry) {
                return this.createError({
                  message: t(
                    'You cannot add Chartink to the exit condition without first adding it to the entry condition',
                  ),
                });
              }
              return true;
            },
          ),
        })}
        onSubmit={values => {
          // If confirmation modal open then we have to proceed for next step
          if (invalidConditionDirection === '') {
            // Check conditions and valid
            //Open confirm modal if invalid otherwise proceed for next step
            if (
              values.positionDirection === 'short' ||
              values.positionDirection === 'both'
            ) {
              if (
                !isValidConditions(
                  values.shortEntryConditions ?? {
                    conditions: [],
                    isConditionValid: false,
                  },
                  'entry',
                )
              ) {
                setInvalidConditionDirection(
                  t('user_bot_startegy.short_entry'),
                );
                return;
              }
              if (
                !isValidConditions(
                  values.shortExitConditions ?? {
                    conditions: [],
                    isConditionValid: false,
                  },
                  'exit',
                )
              ) {
                setInvalidConditionDirection(t('user_bot_startegy.short_exit'));
                return;
              }
            }
            if (
              values.positionDirection === 'long' ||
              values.positionDirection === 'both'
            ) {
              if (
                !isValidConditions(
                  values.longEntryConditions ?? {
                    conditions: [],
                    isConditionValid: false,
                  },
                  'entry',
                )
              ) {
                setInvalidConditionDirection(t('user_bot_startegy.long_entry'));
                return;
              }
              if (
                !isValidConditions(
                  values.longExitConditions ?? {
                    conditions: [],
                    isConditionValid: false,
                  },
                  'exit',
                )
              ) {
                setInvalidConditionDirection(t('user_bot_startegy.long_exit'));
                return;
              }
            }
          }
          handleContinue(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          touched,
          isValid,
          validateForm,
          setTouched,
        }) => {
          // Helper to check if Chartink is present in a condition array
          const hasChartink = (arr: FieldData[] = []) =>
            Array.isArray(arr) &&
            arr.some((c: FieldData) => c.key === 'chartink');
          // Helper to check if any other condition exists
          const hasOtherCondition = (arr: FieldData[] = []) =>
            Array.isArray(arr) &&
            arr.some((c: FieldData) => c.key !== 'chartink');

          // Handler for Chartink button click
          const handleAddChartinkCondition = (
            type: 'long_entry' | 'long_exit' | 'short_entry' | 'short_exit',
          ) => {
            let arr: FieldData[] = [];
            if (type === 'long_entry')
              arr = values.longEntryConditions?.conditions ?? [];
            if (type === 'long_exit')
              arr = values.longExitConditions?.conditions ?? [];
            if (type === 'short_entry')
              arr = values.shortEntryConditions?.conditions ?? [];
            if (type === 'short_exit')
              arr = values.shortExitConditions?.conditions ?? [];

            if (hasChartink(arr)) return; // Already present, do nothing
            if (hasOtherCondition(arr)) {
              // setPendingChartinkType(type); // This state is removed
              // setShowChartinkConfirm(true); // This state is removed
              return;
            }
            // No other condition, add Chartink directly
            // addChartinkToType(type); // Not used in new flow
          };

          // Function to actually add Chartink
          const addChartinkToType = (
            type: 'long_entry' | 'long_exit' | 'short_entry' | 'short_exit',
          ) => {
            const chartinkCondition: FieldData = {
              key: 'chartink',
              type: 'external_signal',
              params: { label: 'chartink' },
            };
            const newValue: FieldDataWithValidate = {
              conditions: [chartinkCondition],
              isConditionValid: true,
            };
            if (type === 'long_entry') {
              setFieldValue('longEntryConditions', newValue, false);
              setTouched({ longEntryConditions: true });
              void validateForm();
            } else if (type === 'long_exit') {
              setFieldValue('longExitConditions', newValue, false);
              if (
                !values.longEntryConditions?.conditions.some(
                  (c: FieldData) => c.key === 'chartink',
                )
              ) {
                setFieldValue('longEntryConditions', newValue, false);
                setTouched({ longEntryConditions: true });
              }
              setTouched({ longExitConditions: true });
              void validateForm();
            } else if (type === 'short_entry') {
              setFieldValue('shortEntryConditions', newValue, false);
              setTouched({ shortEntryConditions: true });
              void validateForm();
            } else if (type === 'short_exit') {
              setFieldValue('shortExitConditions', newValue, false);
              if (
                !values.shortEntryConditions?.conditions.some(
                  (c: FieldData) => c.key === 'chartink',
                )
              ) {
                setFieldValue('shortEntryConditions', newValue, false);
                setTouched({ shortEntryConditions: true });
              }
              setTouched({ shortExitConditions: true });
              void validateForm();
            }
          };

          // Handler for confirming popup
          const handleConfirmChartink = () => {
            // if (pendingChartinkType) { // This state is removed
            //   addChartinkToType(pendingChartinkType); // This state is removed
            //   setPendingChartinkType(null); // This state is removed
            //   setShowChartinkConfirm(false); // This state is removed
            // }
          };
          // Handler for cancelling popup
          const handleCancelChartink = () => {
            // setPendingChartinkType(null); // This state is removed
            // setShowChartinkConfirm(false); // This state is removed
          };

          const targetType = values.targetType
            ? values.targetType === 'percentage'
              ? '%'
              : t('user_bot_startegy.form.points')
            : '';

          return (
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box sx={{ mb: 2, flex: 1 }}>
                <Box className="m-main-spacing">
                  <Grid container columnSpacing={2}>
                    <Grid item xs={12} md={12} lg={6}>
                      <FormControl
                        fullWidth
                        sx={{ mb: 3 }}
                        error={Boolean(
                          touched.positionDirection && errors.positionDirection,
                        )}
                      >
                        <InputLabel id="label-positionDirection">
                          <FormattedMessage id="user_bot_startegy.form.position_direction" />
                        </InputLabel>
                        <Select
                          labelId="label-positionDirection"
                          name="positionDirection"
                          value={values.positionDirection}
                          label={t('user_bot_startegy.form.position_direction')}
                          onChange={e => {
                            setFieldValue('positionDirection', e.target.value);

                            const removeChartink = (arr: FieldData[] = []) =>
                              arr.filter(c => c.key !== 'chartink');
                            setFieldValue('longEntryConditions', {
                              ...values.longEntryConditions,
                              conditions: removeChartink(
                                values.longEntryConditions?.conditions || [],
                              ),
                            });
                            setFieldValue('longExitConditions', {
                              ...values.longExitConditions,
                              conditions: removeChartink(
                                values.longExitConditions?.conditions || [],
                              ),
                            });
                            setFieldValue('shortEntryConditions', {
                              ...values.shortEntryConditions,
                              conditions: removeChartink(
                                values.shortEntryConditions?.conditions || [],
                              ),
                            });
                            setFieldValue('shortExitConditions', {
                              ...values.shortExitConditions,
                              conditions: removeChartink(
                                values.shortExitConditions?.conditions || [],
                              ),
                            });
                            setChartinkEnabled(false); // <-- Chartink reset on direction change

                            setTimeout(() => {
                              void validateForm();
                            }, 0);
                          }}
                          endAdornment={
                            <InputAdornment position="end" sx={{ mr: 1 }}>
                              <CustomTooltip
                                tooltipKey={'eq_position_direction'}
                              >
                                {({ isTooltipAvailable }) =>
                                  isTooltipAvailable ? (
                                    <IconButton>
                                      <InfoOutlinedIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )
                                }
                              </CustomTooltip>
                            </InputAdornment>
                          }
                        >
                          {PositionDirectionData.map(item => (
                            <MenuItem value={item.value} key={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.positionDirection &&
                          errors.positionDirection && (
                            <FormHelperText error>
                              {errors.positionDirection}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      lg={6}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mb: 2,
                      }}
                    >
                      {isChartinkEnabled && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CustomTooltip tooltipKey={'eq_chartink'}>
                            {({ isTooltipAvailable }) =>
                              isTooltipAvailable ? (
                                <IconButton size="small" sx={{ mr: 1 }}>
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <></>
                              )
                            }
                          </CustomTooltip>
                          <Box
                            component="img"
                            src={chartinklogo}
                            alt="Chartink"
                            sx={{
                              height: 20,
                              cursor: chartinkEnabled
                                ? 'not-allowed'
                                : 'pointer',
                              opacity: chartinkEnabled ? 0.5 : 1,
                              '&:hover': {
                                opacity: chartinkEnabled ? 0.5 : 0.8,
                              },
                            }}
                            onClick={() => {
                              if (!chartinkEnabled) {
                                // Always show the unified Chartink dialog
                                setShowChartinkDialog(true);
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {(values.positionDirection === 'long' ||
                  values.positionDirection === 'both') && (
                  <>
                    <PositionDirectionCondition
                      title={`${t(
                        'user_bot_startegy.form.long_entry_condition',
                      )}`}
                      tooltipKey="eq_long_entry_condition"
                      conditionsData={
                        values.longEntryConditions?.conditions ?? []
                      }
                      isConditionValid={
                        values.longEntryConditions?.isConditionValid ?? false
                      }
                      setConditionsDataWithValid={(
                        values: FieldDataWithValidate | FieldDataConditions,
                      ) => setFieldValue('longEntryConditions', values)}
                      error={
                        typeof errors.longEntryConditions === 'string'
                          ? errors.longEntryConditions
                          : typeof errors.longEntryConditions === 'object' &&
                            errors.longEntryConditions !== null &&
                            'conditions' in errors.longEntryConditions &&
                            typeof (
                              errors.longEntryConditions as {
                                conditions?: unknown;
                              }
                            ).conditions === 'string'
                          ? (
                              errors.longEntryConditions as {
                                conditions: string;
                              }
                            ).conditions
                          : ''
                      }
                      updateCopiedStrategy={setCopiedStrategy}
                      copiedStrategy={copiedStrategy}
                      defaultResolution={defaultResolution}
                      chartinkDisabled={false}
                      onChartinkRemoved={handleChartinkRemoved}
                      positionDirection={values.positionDirection}
                      blockType="entry"
                      entryType="long"
                      onRemoveChartinkBothEntry={handleRemoveChartinkBothEntry}
                    />
                    <PositionDirectionCondition
                      title={`${t(
                        'user_bot_startegy.form.long_exit_condition',
                      )}(${t('user_bot_startegy.optional')})`}
                      tooltipKey="eq_long_exit_condition"
                      conditionsData={
                        values.longExitConditions?.conditions ?? []
                      }
                      isConditionValid={
                        values.longExitConditions?.isConditionValid ?? false
                      }
                      setConditionsDataWithValid={(
                        values: FieldDataWithValidate | FieldDataConditions,
                      ) => setFieldValue('longExitConditions', values)}
                      // Show both object-level and conditions-level errors
                      error={
                        typeof errors.longExitConditions === 'string'
                          ? errors.longExitConditions
                          : typeof errors.longExitConditions === 'object' &&
                            errors.longExitConditions !== null &&
                            'conditions' in errors.longExitConditions &&
                            typeof (
                              errors.longExitConditions as {
                                conditions?: unknown;
                              }
                            ).conditions === 'string'
                          ? (
                              errors.longExitConditions as {
                                conditions: string;
                              }
                            ).conditions
                          : ''
                      }
                      updateCopiedStrategy={setCopiedStrategy}
                      copiedStrategy={copiedStrategy}
                      defaultResolution={defaultResolution}
                      chartinkDisabled={false}
                      onChartinkRemoved={handleChartinkRemoved}
                      positionDirection={values.positionDirection}
                      onRemoveChartinkBothExit={handleRemoveChartinkBothExit}
                      // Exit block ke liye blockType='exit' bhej rahe hain
                      blockType="exit"
                      exitType="long"
                    />
                  </>
                )}
                {(values.positionDirection === 'short' ||
                  values.positionDirection === 'both') && (
                  <>
                    <PositionDirectionCondition
                      title={`${t(
                        'user_bot_startegy.form.short_entry_condition',
                      )}`}
                      tooltipKey="eq_short_entry_condition"
                      conditionsData={
                        values.shortEntryConditions?.conditions ?? []
                      }
                      isConditionValid={
                        values.shortEntryConditions?.isConditionValid ?? false
                      }
                      setConditionsDataWithValid={(
                        values: FieldDataWithValidate | FieldDataConditions,
                      ) => setFieldValue('shortEntryConditions', values)}
                      error={
                        typeof errors.shortEntryConditions === 'string'
                          ? errors.shortEntryConditions
                          : typeof errors.shortEntryConditions === 'object' &&
                            errors.shortEntryConditions !== null &&
                            'conditions' in errors.shortEntryConditions &&
                            typeof (
                              errors.shortEntryConditions as {
                                conditions?: unknown;
                              }
                            ).conditions === 'string'
                          ? (
                              errors.shortEntryConditions as {
                                conditions: string;
                              }
                            ).conditions
                          : ''
                      }
                      updateCopiedStrategy={setCopiedStrategy}
                      copiedStrategy={copiedStrategy}
                      defaultResolution={defaultResolution}
                      chartinkDisabled={false}
                      onChartinkRemoved={handleChartinkRemoved}
                      positionDirection={values.positionDirection}
                      onRemoveChartinkBothExit={handleRemoveChartinkBothExit}
                      // Entry block ke liye blockType='entry' bhej rahe hain
                      blockType="entry"
                      entryType="short"
                      onRemoveChartinkBothEntry={handleRemoveChartinkBothEntry}
                    />
                    <PositionDirectionCondition
                      title={`${t(
                        'user_bot_startegy.form.short_exit_condition',
                      )}(${t('user_bot_startegy.optional')})`}
                      tooltipKey="eq_short_exit_condition"
                      conditionsData={
                        values.shortExitConditions?.conditions ?? []
                      }
                      isConditionValid={
                        values.shortExitConditions?.isConditionValid ?? false
                      }
                      setConditionsDataWithValid={(
                        values: FieldDataWithValidate | FieldDataConditions,
                      ) => setFieldValue('shortExitConditions', values)}
                      // Show both object-level and conditions-level errors
                      error={
                        typeof errors.shortExitConditions === 'string'
                          ? errors.shortExitConditions
                          : typeof errors.shortExitConditions === 'object' &&
                            errors.shortExitConditions !== null &&
                            'conditions' in errors.shortExitConditions &&
                            typeof (
                              errors.shortExitConditions as {
                                conditions?: unknown;
                              }
                            ).conditions === 'string'
                          ? (
                              errors.shortExitConditions as {
                                conditions: string;
                              }
                            ).conditions
                          : ''
                      }
                      updateCopiedStrategy={setCopiedStrategy}
                      copiedStrategy={copiedStrategy}
                      defaultResolution={defaultResolution}
                      chartinkDisabled={false}
                      onChartinkRemoved={handleChartinkRemoved}
                      positionDirection={values.positionDirection}
                      onRemoveChartinkBothExit={handleRemoveChartinkBothExit}
                      // Exit block ke liye blockType='exit' bhej rahe hain
                      blockType="exit"
                      exitType="short"
                    />
                  </>
                )}

                <Box className="m-main-spacing">
                  <Box sx={{ mb: 3 }}>
                    <ItemHead
                      variant="medium"
                      title={t('user_bot_startegy.form.target_and_stoploss')}
                    />
                    <ItemSeparator variant="2px" />
                  </Box>
                  <Grid container columnSpacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                      <FormControl
                        fullWidth
                        sx={{ mb: 3 }}
                        error={Boolean(touched.targetType && errors.targetType)}
                      >
                        <InputLabel id="label-targetType">
                          <FormattedMessage id="user_bot_startegy.form.target_stop_loss_type" />
                        </InputLabel>
                        <Select
                          labelId="label-targetType"
                          name="targetType"
                          value={values.targetType}
                          label={t(
                            'user_bot_startegy.form.target_stop_loss_type',
                          )}
                          onChange={e => {
                            setFieldValue('targetType', e.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position="end" sx={{ mr: 1 }}>
                              <CustomTooltip tooltipKey={'eq_sl_target_type'}>
                                {({ isTooltipAvailable }) =>
                                  isTooltipAvailable ? (
                                    <IconButton>
                                      <InfoOutlinedIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )
                                }
                              </CustomTooltip>
                            </InputAdornment>
                          }
                        >
                          {RiskTypeData.map(item => (
                            <MenuItem value={item.value} key={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.targetType && errors.targetType && (
                          <FormHelperText error>
                            {errors.targetType}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: { xs: 'block', lg: 'none' } }}
                    ></Grid>

                    <Grid item xs={6} lg={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.target && errors.target)}
                        variant="outlined"
                        sx={{ mb: 3 }}
                      >
                        <InputLabel htmlFor="label-target">
                          {t('user_bot_startegy.form.target')} {targetType}
                        </InputLabel>
                        <OutlinedInput
                          id="label-target"
                          type="number"
                          name="target"
                          value={values.target}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          label={`${t(
                            'user_bot_startegy.form.target',
                          )} ${targetType}`}
                          inputProps={{ min: 0 }}
                          endAdornment={
                            <InputAdornment position="end">
                              <CustomTooltip tooltipKey={'eq_target'}>
                                {({ isTooltipAvailable }) =>
                                  isTooltipAvailable ? (
                                    <IconButton>
                                      <InfoOutlinedIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )
                                }
                              </CustomTooltip>
                            </InputAdornment>
                          }
                        />
                        {touched.target && errors.target && (
                          <FormHelperText error>{`${t(
                            'user_bot_startegy.form.target',
                          )} ${targetType} ${t(
                            'user_backtesting.valid_msg.is_required',
                          )}`}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} lg={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.stoploss && errors.stoploss)}
                        variant="outlined"
                        sx={{ mb: 3 }}
                      >
                        <InputLabel htmlFor="label-stoploss">
                          {t('user_bot_startegy.form.stoploss')} {targetType}
                        </InputLabel>
                        <OutlinedInput
                          id="label-stoploss"
                          type="number"
                          name="stoploss"
                          value={values.stoploss}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          label={`Stoploss ${targetType}`}
                          inputProps={{ min: 0 }}
                          endAdornment={
                            <InputAdornment position="end">
                              <CustomTooltip tooltipKey={'eq_stoploss'}>
                                {({ isTooltipAvailable }) =>
                                  isTooltipAvailable ? (
                                    <IconButton>
                                      <InfoOutlinedIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )
                                }
                              </CustomTooltip>
                            </InputAdornment>
                          }
                        />
                        {touched.stoploss && errors.stoploss && (
                          <FormHelperText error>
                            {`${t(
                              'user_bot_startegy.form.stoploss',
                            )} ${targetType} ${t(
                              'user_backtesting.valid_msg.is_required',
                            )}`}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    {/* <FeatureFlag
                      features={[FEATURES.IS_TRAILING_STOPLOSS]}
                      fallback={<></>}
                    >
                      <Grid item xs={6} lg={4}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', md: 'start' },
                          }}
                        >
                          <FormControlLabel
                            sx={{ mb: { xs: 3, md: 0 } }}
                            control={
                              <Checkbox
                                sx={{ p: '7px' }}
                                checked={values.isTrailStoploss}
                                onChange={e =>
                                  setFieldValue(
                                    'isTrailStoploss',
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={`${t(
                              'user_bot_startegy.form.trail_stoploss',
                            )} ${targetType}`}
                          />
                        </Box>
                      </Grid>

                      {values.isTrailStoploss && (
                        <Grid item xs={6} lg={4}>
                          <FormControl
                            fullWidth
                            error={Boolean(
                              touched.trailStoploss && errors.trailStoploss,
                            )}
                            variant="outlined"
                            sx={{ mb: 3 }}
                          >
                            <InputLabel htmlFor="label-trailStoploss">
                              {t('user_bot_startegy.form.trail_stoploss')}{' '}
                              {targetType}
                            </InputLabel>
                            <OutlinedInput
                              id="label-trailStoploss"
                              type="number"
                              name="trailStoploss"
                              value={values.trailStoploss}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              label={`Trail Stoploss ${targetType}`}
                              inputProps={{ min: 0, step: '0.25' }}
                            />
                            {touched.trailStoploss && errors.trailStoploss && (
                              <FormHelperText error>
                                {`${t(
                                  'user_bot_startegy.form.trail_stoploss',
                                )} ${targetType} ${t(
                                  'user_backtesting.valid_msg.is_required',
                                )} `}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      )}
                    </FeatureFlag> */}
                    <FeatureFlag
                      features={[FEATURES.IS_TRAILING_STOPLOSS]}
                      fallback={<></>}
                    >
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl
                          fullWidth
                          sx={{ mb: 3 }}
                          error={Boolean(
                            touched.trailing_sl_enabled &&
                              errors.trailing_sl_enabled,
                          )}
                        >
                          <InputLabel id="label-trailing_sl_enabled">
                            Enable Trail Stop Loss
                          </InputLabel>
                          <Select
                            labelId="label-trailing_sl_enabled"
                            name="trailing_sl_enabled"
                            value={Boolean(values.trailing_sl_enabled) === true}
                            label="Enable Trail Stop Loss"
                            onChange={e => {
                              if (e.target.value === 'false') {
                                setFieldValue('trailingValue', 1);
                                setFieldValue('trailingTriggerValue', 1);
                              }
                              setFieldValue(
                                'trailing_sl_enabled',
                                e.target.value === 'true',
                              );
                            }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                          {touched.trailing_sl_enabled &&
                            errors.trailing_sl_enabled && (
                              <FormHelperText>
                                {errors.trailing_sl_enabled}
                              </FormHelperText>
                            )}
                        </FormControl>
                      </Grid>

                      {/* Conditionally render additional input fields when "yes" is selected */}
                      {values.trailing_sl_enabled && (
                        <>
                          <Grid item xs={6} lg={4}>
                            <FormControl
                              fullWidth
                              error={Boolean(
                                touched.trailingTriggerValue &&
                                  errors.trailingTriggerValue,
                              )}
                              variant="outlined"
                              sx={{ mb: 3 }}
                            >
                              <InputLabel htmlFor="label-trailingTriggerValue">
                                {t(
                                  'user_bot_startegy.form.trailingTriggerValue',
                                )}{' '}
                                {targetType}
                              </InputLabel>
                              <OutlinedInput
                                id="label-trailingTriggerValue"
                                type="number"
                                name="trailingTriggerValue"
                                value={values.trailingTriggerValue}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                label={`trailingTriggerValue ${targetType}`}
                                inputProps={{ min: 0 }}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <CustomTooltip
                                      tooltipKey={'trailing_sl_trigger'}
                                    >
                                      {({ isTooltipAvailable }) =>
                                        isTooltipAvailable ? (
                                          <IconButton>
                                            <InfoOutlinedIcon />
                                          </IconButton>
                                        ) : (
                                          <></>
                                        )
                                      }
                                    </CustomTooltip>
                                  </InputAdornment>
                                }
                              />
                              {touched.trailingTriggerValue &&
                                errors.trailingTriggerValue && (
                                  <FormHelperText error>{`${t(
                                    'user_bot_startegy.form.trailingTriggerValue',
                                  )} ${targetType} ${t(
                                    'user_backtesting.valid_msg.is_required',
                                  )}`}</FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} lg={4}>
                            <FormControl
                              fullWidth
                              error={Boolean(
                                touched.trailingValue && errors.trailingValue,
                              )}
                              variant="outlined"
                              sx={{ mb: 3 }}
                            >
                              <InputLabel htmlFor="label-trailingValue">
                                {t('user_bot_startegy.form.trailingValue')}{' '}
                                {targetType}
                              </InputLabel>
                              <OutlinedInput
                                id="label-trailingValue"
                                type="number"
                                name="trailingValue"
                                value={values.trailingValue}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                label={`trailingValue ${targetType}`}
                                inputProps={{ min: 0 }}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <CustomTooltip tooltipKey={'trailing_sl'}>
                                      {({ isTooltipAvailable }) =>
                                        isTooltipAvailable ? (
                                          <IconButton>
                                            <InfoOutlinedIcon />
                                          </IconButton>
                                        ) : (
                                          <></>
                                        )
                                      }
                                    </CustomTooltip>
                                  </InputAdornment>
                                }
                              />
                              {touched.trailingValue &&
                                errors.trailingValue && (
                                  <FormHelperText error>
                                    {`${t(
                                      'user_bot_startegy.form.trailingValue',
                                    )} ${targetType} ${t(
                                      'user_backtesting.valid_msg.is_required',
                                    )}`}
                                  </FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </FeatureFlag>
                  </Grid>
                </Box>
              </Box>
              <Box
                className="m-main-spacing"
                sx={{
                  mb: 0,
                  textAlign: 'center',
                  display: { xs: 'block', md: 'flex' },
                  justifyContent: 'center',
                  flexDirection: 'row-reverse',
                }}
              >
                <MuiButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth={!isMdUp}
                  sx={{
                    mb: { xs: 2, md: 0 },
                  }}
                  disabled={!isValid}
                >
                  <FormattedMessage id="buttons.continue" />
                </MuiButton>
                <MuiButton
                  variant="contained"
                  color="secondary"
                  fullWidth={!isMdUp}
                  onClick={handleBack}
                  sx={{
                    mr: { xs: 0, md: 2 },
                  }}
                >
                  <FormattedMessage id="buttons.back" />
                </MuiButton>
              </Box>

              <SnakbarMessageModal
                open={invalidConditionDirection !== ''}
                handleClose={() => handleInvalidConditionDirection()}
                body={t('user_bot_startegy.entered_invalid_condition', {
                  name: invalidConditionDirection,
                })}
                // body={`You have not entered a valid ${invalidConditionDirection} condition.`}
                footer={
                  <>
                    <Box display="flex" justifyContent="end">
                      <Box>
                        <MuiButton
                          variant="contained"
                          color="secondary"
                          sx={{ mr: 1 }}
                          onClick={handleInvalidConditionDirection}
                        >
                          <FormattedMessage id="buttons.cancel" />
                        </MuiButton>
                      </Box>
                      <Box>
                        <MuiButton
                          variant="contained"
                          color="primary"
                          onClick={handleSubmit}
                        >
                          <FormattedMessage id="buttons.continue_anyway" />
                        </MuiButton>
                      </Box>
                    </Box>
                  </>
                }
              />
            </Box>
          );
        }}
      </Formik>
    </>
  );
}
